import React, { useEffect, useRef, useState } from 'react';
import { useFaceLandmarker } from '../hooks/useFaceLandmarker';
import { useObjectDetector } from '../hooks/useObjectDetector';
import { usePoseLandmarker } from '../hooks/usePoseLandmarker';
import { useHandLandmarker } from '../hooks/useHandLandmarker';
import { computeHomography, PAPER_DST, type Point2D } from '../engine/Homography';
import type { PoseHeadEstimate } from '../hooks/usePoseLandmarker';
import type { PoseAuxInfo, PhoneGripAuxInfo, PaperZoneState } from '../hooks/useFaceLandmarker';
import type { Detection } from '../hooks/useObjectDetector';

const VIDEO_ID = 'synthVideo';
const OVERLAY_ID = 'synthOverlay';
const HAND_OVERLAY_ID = 'synthHandOverlay';
const SAMPLE_MP4 = '/static/proctoring-react/sample/synth_demo_0424_tagged.mp4';

/**
 * Estimated book corners for the 0424 sample (normalized [0,1]).
 * Order: TL, TR, BR, BL — matches PAPER_DST.
 *
 * 책의 사인펜 fiducial 태그 위치 가정값. 합성 시 ffmpeg로 (50, 470),
 * (920, 470), (920, 680), (50, 680) 픽셀 좌표(1280×720)에 마커를
 * 배치했으므로 동일 좌표를 정규화로 환산해 calib baseline으로 사용한다.
 */
const SAMPLE_CORNERS: Point2D[] = [
  { x: 50 / 1280,  y: 470 / 720 },
  { x: 920 / 1280, y: 470 / 720 },
  { x: 920 / 1280, y: 680 / 720 },
  { x: 50 / 1280,  y: 680 / 720 },
];

/**
 * SynthTest mode — feeds a pre-rendered exam recording (with synthetic
 * fiducial tags composited onto book corners) through the same drsimon
 * recognition pipeline as live webcam mode. Used to validate that the
 * 4-corner marker detection + homography + 3-zone classification works
 * on real-world frames without requiring a live camera setup.
 *
 * Entry: append `?view=synthtest` to the URL.
 */
export function SynthTestApp() {
  const detectionsRef = useRef<Detection[]>([]);
  const paperHomographyRef = useRef<Float64Array | null>(null);
  const paperCornersRef = useRef<Point2D[]>([...SAMPLE_CORNERS]);
  const calibHomographyRef = useRef<Float64Array | null>(null);
  const calibCornersRef = useRef<Point2D[]>([...SAMPLE_CORNERS]);
  const poseHeadRef = useRef<PoseHeadEstimate>({
    yaw: 0, pitch: 0, noseX: 0.5, noseY: 0.3, valid: false,
  });
  const poseAuxRef = useRef<PoseAuxInfo | null>(null);
  const phoneGripRef = useRef<PhoneGripAuxInfo | null>(null);
  const paperZoneStateRef = useRef<PaperZoneState | null>(null);
  const dwellRef = useRef({ inside: 0, boundary: 0, outside: 0, total: 0 });
  const lastSampleRef = useRef(0);

  // Live-tunable boundary margin — fed into useFaceLandmarker via ref so the
  // detect loop sees the latest value without re-mounting.
  const [margin, setMargin] = useState(0.15);
  const marginRef = useRef(margin);
  useEffect(() => { marginRef.current = margin; }, [margin]);

  // Seed homography from the assumed calibration corners so the paper
  // outline is drawn from frame 1 even before MarkerDetector locks on.
  useEffect(() => {
    const H = computeHomography(
      SAMPLE_CORNERS as [Point2D, Point2D, Point2D, Point2D],
      PAPER_DST,
    );
    if (H) {
      paperHomographyRef.current = H;
      calibHomographyRef.current = new Float64Array(H);
    }
  }, []);

  useFaceLandmarker(
    VIDEO_ID, OVERLAY_ID, detectionsRef,
    paperHomographyRef, paperCornersRef,
    poseHeadRef, calibHomographyRef, calibCornersRef,
    poseAuxRef, phoneGripRef, paperZoneStateRef, marginRef,
  );

  const pose = usePoseLandmarker(VIDEO_ID);
  poseHeadRef.current = pose.head;
  poseAuxRef.current = { ...pose.head, shoulderAngle: pose.shoulderAngle };

  const obj = useObjectDetector(VIDEO_ID, true);
  detectionsRef.current = obj.detections;

  const hand = useHandLandmarker(VIDEO_ID, HAND_OVERLAY_ID, true);
  phoneGripRef.current = {
    grip: hand.phoneGrip,
    sustainFrames: hand.gripSustainFrames,
  };

  // ── Side panel diagnostics + dwell tracker ──
  // Sample the paperZoneStateRef every 100ms and accumulate per-zone time.
  // The integrating loop is kept independent of the detection rate so the
  // displayed inside/boundary/outside ratios stay stable even if face
  // detection drops a frame.
  const [tick, setTick] = useState(0);
  useEffect(() => {
    lastSampleRef.current = performance.now();
    const id = setInterval(() => {
      const now = performance.now();
      const dt = Math.min(500, now - lastSampleRef.current);
      lastSampleRef.current = now;
      const z = paperZoneStateRef.current?.zone;
      if (z) {
        dwellRef.current.total += dt;
        if (z === 'confident_inside') dwellRef.current.inside += dt;
        else if (z === 'boundary') dwellRef.current.boundary += dt;
        else dwellRef.current.outside += dt;
      }
      setTick(t => t + 1);
    }, 100);
    return () => clearInterval(id);
  }, []);

  const resetDwell = () => {
    dwellRef.current = { inside: 0, boundary: 0, outside: 0, total: 0 };
    lastSampleRef.current = performance.now();
  };

  const dw = dwellRef.current;
  const pct = (n: number) => dw.total > 0 ? (n / dw.total * 100) : 0;
  const insidePct = pct(dw.inside);
  const boundaryPct = pct(dw.boundary);
  const outsidePct = pct(dw.outside);

  // Compare current paperCorners vs the calibration baseline to surface
  // how much per-frame marker re-detection has drifted. RMS pixel error
  // is the simplest defendable metric.
  let cornerDriftPx = 0;
  if (paperCornersRef.current.length === 4) {
    let sumSq = 0;
    for (let i = 0; i < 4; i++) {
      const dx = (paperCornersRef.current[i].x - SAMPLE_CORNERS[i].x) * 1280;
      const dy = (paperCornersRef.current[i].y - SAMPLE_CORNERS[i].y) * 720;
      sumSq += dx * dx + dy * dy;
    }
    cornerDriftPx = Math.sqrt(sumSq / 4);
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, background: '#0b0d10',
      display: 'flex', color: '#e6edf3',
      font: '14px Pretendard, system-ui, sans-serif',
    }}>
      <div style={{
        position: 'relative', flex: 1, minWidth: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <div className="video-wrap" style={{
          position: 'relative', aspectRatio: '16 / 9',
          width: '100%', height: '100%',
          maxWidth: '100%', maxHeight: '100%',
        }}>
          <video
            id={VIDEO_ID}
            src={SAMPLE_MP4}
            autoPlay loop muted playsInline
            style={{
              position: 'absolute', inset: 0,
              width: '100%', height: '100%', objectFit: 'contain',
            }}
          />
          <canvas
            id={OVERLAY_ID}
            style={{
              position: 'absolute', inset: 0,
              width: '100%', height: '100%',
              objectFit: 'contain', pointerEvents: 'none',
            }}
          />
          <canvas
            id={HAND_OVERLAY_ID}
            style={{
              position: 'absolute', inset: 0,
              width: '100%', height: '100%',
              objectFit: 'contain', pointerEvents: 'none',
            }}
          />
        </div>
      </div>

      <aside style={{
        width: 320, padding: '16px 18px',
        background: '#11151a', borderLeft: '1px solid #1f2630',
        overflowY: 'auto',
      }}>
        <h2 style={{ margin: 0, font: '700 18px Pretendard, sans-serif' }}>
          SynthTest
        </h2>
        <p style={{ opacity: 0.65, margin: '4px 0 16px', fontSize: 12 }}>
          0424 webcam recording + synthesized fiducial tags at book corners.
        </p>

        <Section title="Marker calibration (assumed)">
          <KV k="TL" v={fmt(SAMPLE_CORNERS[0])} />
          <KV k="TR" v={fmt(SAMPLE_CORNERS[1])} />
          <KV k="BR" v={fmt(SAMPLE_CORNERS[2])} />
          <KV k="BL" v={fmt(SAMPLE_CORNERS[3])} />
        </Section>

        <Section title="Per-frame re-detection">
          <KV k="Calib H present" v={calibHomographyRef.current ? 'yes' : 'no'} />
          <KV k="Live H present" v={paperHomographyRef.current ? 'yes' : 'no'} />
          <KV k="Corner RMS drift" v={`${cornerDriftPx.toFixed(2)} px`} />
        </Section>

        <Section title="Detections">
          <KV k="Objects" v={String(obj.detections.length)} />
          <KV k="Hand count" v={String(hand.handCount)} />
          <KV k="Phone grip" v={hand.phoneGrip.isGrip ? `yes (${hand.gripSustainFrames}f)` : 'no'} />
          <KV k="Pose valid" v={pose.head.valid ? 'yes' : 'no'} />
        </Section>

        <Section title="Gaze · paper zone (filtered)">
          <KV k="Current" v={paperZoneStateRef.current?.zone ?? '—'} />
          <KV k="Region" v={paperZoneStateRef.current?.region ?? '—'} />
          <KV k="Off-screen" v={paperZoneStateRef.current?.offScreen ? 'yes' : 'no'} />
        </Section>

        <Section title="3-zone dwell (cumulative)">
          <ZoneBar label="inside"   pct={insidePct}   color="#22c55e" />
          <ZoneBar label="boundary" pct={boundaryPct} color="#eab308" />
          <ZoneBar label="outside"  pct={outsidePct}  color="#ef4444" />
          <KV k="Total tracked" v={`${(dw.total / 1000).toFixed(1)}s`} />
          <button
            onClick={resetDwell}
            style={{
              marginTop: 6, padding: '4px 8px', fontSize: 12,
              background: '#1f2630', color: '#e6edf3',
              border: '1px solid #2a3340', borderRadius: 4, cursor: 'pointer',
            }}
          >Reset dwell</button>
        </Section>

        <Section title="Boundary margin (tunable)">
          <KV k="Current" v={margin.toFixed(2)} />
          <input
            type="range" min={0.05} max={0.35} step={0.01}
            value={margin}
            onChange={e => { setMargin(Number(e.target.value)); resetDwell(); }}
            style={{ width: '100%' }}
          />
          <p style={{ margin: 0, fontSize: 11, opacity: 0.6 }}>
            Wider margin → broader gray (boundary) band. Reset auto-fires on change.
          </p>
        </Section>

        <Section title="Sample">
          <p style={{ margin: 0, fontSize: 12, opacity: 0.7 }}>
            Markers were composited via ffmpeg overlay at book bbox corners
            (TL/TR/BR/BL). Drift &gt; 0 means MarkerDetector locked onto a
            slightly different position than the assumed baseline.
          </p>
        </Section>

        <p style={{ marginTop: 16, opacity: 0.4, fontSize: 11 }}>tick {tick}</p>
      </aside>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section style={{ marginBottom: 16 }}>
      <div style={{
        textTransform: 'uppercase', letterSpacing: 0.6,
        fontSize: 11, opacity: 0.55, marginBottom: 6,
      }}>{title}</div>
      <div style={{ display: 'grid', rowGap: 4 }}>{children}</div>
    </section>
  );
}

function KV({ k, v }: { k: string; v: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
      <span style={{ opacity: 0.7 }}>{k}</span>
      <span style={{ fontVariantNumeric: 'tabular-nums' }}>{v}</span>
    </div>
  );
}

function fmt(p: Point2D): string {
  return `(${p.x.toFixed(3)}, ${p.y.toFixed(3)})`;
}

function ZoneBar({ label, pct, color }: { label: string; pct: number; color: string }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '90px 1fr 56px', alignItems: 'center', gap: 6 }}>
      <span style={{ fontSize: 12, opacity: 0.8 }}>{label}</span>
      <div style={{ height: 8, background: '#1f2630', borderRadius: 4, overflow: 'hidden' }}>
        <div style={{ width: `${Math.min(100, pct)}%`, height: '100%', background: color }} />
      </div>
      <span style={{ fontSize: 12, fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>
        {pct.toFixed(1)}%
      </span>
    </div>
  );
}

export default SynthTestApp;
